import type { BlockRegistry } from "../registry/registry.js";
import type { ResolvedDocument, ResolvedNode } from "../resolve/resolved.js";
import type { InlineNode, RichTextDoc } from "../richtext/model.js";
import type { Expr } from "../expr/ast.js";
import { parseExpression } from "../expr/parser.js";
import { issue, type Issue } from "./issues.js";

export interface OutputCheckOptions {
  /** A document resolved in preview mode against the data you intend to ship. */
  resolved: ResolvedDocument;
  registry: BlockRegistry;
  /** Names the data set in the messages, e.g. "Edge case". */
  dataLabel?: string;
  /** Cap on issues returned, so a broken data set cannot flood the UI. */
  limit?: number;
}

/**
 * Reports what this particular data set does to the report: fields that come out
 * blank, blocks that render nothing, sections with no content at all.
 *
 * This is deliberately separate from `validateTemplate`, which asks whether the
 * design is sound and never looks at data. A template can be perfectly valid and
 * still produce "Values as at ." for a client whose review has no end date, and
 * only one of those two checks can ever tell you so.
 *
 * Three things it does not complain about, because complaining would train
 * people to ignore it:
 *
 * - Content hidden by a false condition. A preview resolution has already
 *   dropped those nodes, so they cannot be reached from here at all.
 * - An expression that already handles the empty case — a `default:` fallback
 *   or a conditional. Warning about those would be warning about the fix.
 * - Zero, false and empty-but-intended lists. `0` is a value; a table with an
 *   empty message is a designed outcome, not an accident.
 * - Page-dependent tokens, which have no value until the paginator runs.
 */
export function checkOutput(opts: OutputCheckOptions): Issue[] {
  const { resolved, registry } = opts;
  const limit = opts.limit ?? 60;
  const out: Issue[] = [];
  const seen = new Set<string>();
  const suffix = opts.dataLabel ? ` in “${opts.dataLabel}”` : " in this data set";

  const push = (i: Issue) => {
    const key = `${i.nodeId ?? ""}|${i.prop ?? ""}|${i.message}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(i);
  };

  // A document nobody has put anything in yet is not a data problem. The
  // structural pass already greets it with "The document has no content yet";
  // warning again, once per empty section and once per sample set, tells the
  // designer twice over about the blank page they are looking at.
  const blankDocument = resolved.body.every((s) => s.children.length === 0);

  const regions: Array<[ResolvedNode[], Issue["region"]]> = [
    [resolved.body, "body"],
    [resolved.header.default, "header"],
    [resolved.header.firstPage ?? [], "header"],
    [resolved.footer.default, "footer"],
    [resolved.footer.firstPage ?? [], "footer"]
  ];

  for (const [nodes, region] of regions) for (const n of nodes) visit(n, region);

  return out.slice(0, limit);

  function visit(node: ResolvedNode, region: Issue["region"]) {
    // Repeater iterations all come from one template node; reporting the first
    // is enough to get the designer to the block, and `seen` collapses the rest.
    if (!node.placeholder) inspect(node, region);
    for (const c of node.children) visit(c, region);
  }

  function inspect(node: ResolvedNode, region: Issue["region"]) {
    const def = registry.get(node.type);
    const name = node.label || def?.name || node.type;
    const where = { nodeId: node.nodeId, region, source: "output" as const };

    // 1. A whole prop bound to a single field that came back with nothing.
    for (const [prop, b] of Object.entries(node.bindings)) {
      if (b.kind !== "value" && b.kind !== "template") continue;
      if (node.textSegments[prop]?.some((s) => s.deferred)) continue;
      if (handlesEmpty(b.expr)) continue;
      const value = getPath(node.props, prop);
      if (!isBlank(value)) continue;
      // Say what is actually missing. One empty optional field — a signature
      // block with no qualifications line, a KPI with no caption — is a gap in
      // the block, not the block coming out blank, and reporting it as the
      // latter sends the designer looking for a problem that is not there. The
      // block is only blank when this field was all it had to draw.
      const blank = !drawsWithout(node, prop);
      push(
        issue(
          "warning",
          "empty-value",
          blank ? `“${b.label}” has no value${suffix}, so ${name} renders blank` : `“${b.label}” has no value${suffix} and is left out of ${name}`,
          { ...where, prop }
        )
      );
    }

    // 2. A token inside a sentence, where the sentence survives and the value
    //    does not. This is what produces "Values as at ." and "contact X on or".
    for (const [prop, segments] of Object.entries(node.textSegments)) {
      if (node.bindings[prop]) continue; // already reported whole, above
      for (const s of segments) {
        if (s.kind !== "token" || s.deferred || !s.expr) continue;
        if (s.value === undefined || !isBlank(s.value)) continue;
        if (handlesEmpty(s.expr)) continue;
        push(issue("warning", "empty-value", `“${s.label ?? short(s.expr)}” is empty${suffix}, leaving a gap in ${name}`, { ...where, prop }));
      }
    }

    // 3. The same, for tokens inside rich text.
    for (const [prop, doc] of Object.entries(node.richText)) {
      for (const inline of inlinesOf(doc)) {
        if (inline.type !== "token" || inline.deferred) continue;
        if (inline.value === undefined || !isBlank(inline.value)) continue;
        if (handlesEmpty(inline.expr)) continue;
        push(issue("warning", "empty-value", `“${inline.label ?? short(inline.expr)}” is empty${suffix}, leaving a gap in ${name}`, { ...where, prop }));
      }
    }

    // 4. A block that carries text and ends up with none of it. Only where it
    //    has no children: a card with an empty title and three paragraphs in it
    //    is a card with an empty title, not an empty card.
    if (node.children.length === 0 && !hasOwnContent(node) && carriesText(node)) {
      push(issue("warning", "empty-content", `${name} renders nothing${suffix}`, where));
    }

    // 5. A section that contains nothing a reader would see. Worth its own
    //    message because the heading it should have had is missing too.
    if (node.role === "section" && !blankDocument && !subtreeHasContent(node)) {
      push(issue("warning", "empty-content", `Section “${name}” has no visible content${suffix}`, where));
    }
  }

  /**
   * Text this node renders itself, ignoring its children. `skip` leaves one
   * prop out, which is how a missing field is told apart from a blank block.
   */
  function ownText(node: ResolvedNode, skip?: string): string {
    let text = "";
    for (const [prop, segments] of Object.entries(node.textSegments)) {
      if (prop === skip) continue;
      for (const s of segments) text += s.kind === "text" ? s.text : s.deferred ? "1" : s.value ?? "";
    }
    for (const [prop, doc] of Object.entries(node.richText)) {
      if (prop === skip) continue;
      // Tokens count as the value they resolved to, not as their expression:
      // the question is what the reader ends up seeing.
      for (const inline of inlinesOf(doc)) text += inline.type === "token" ? (inline.deferred ? "1" : inline.value ?? "") : inline.type === "text" ? inline.text : "";
    }
    for (const [prop, b] of Object.entries(node.bindings)) {
      if (prop === skip) continue;
      if (b.kind === "value") text += String(getPath(node.props, prop) ?? "");
      else if (b.kind === "collection") {
        const v = getPath(node.props, prop);
        if (Array.isArray(v) && v.length) text += "1";
      }
    }
    return text.trim();
  }

  /** Does this block still draw something with `prop` left out of it? */
  function drawsWithout(node: ResolvedNode, prop: string): boolean {
    if (ownText(node, prop) !== "") return true;
    // Anything the block draws that is not words counts too: a signature block
    // is still a signature block with a rule and a date on it.
    return node.children.some(subtreeHasContent) || staticProps(node, prop);
  }

  /**
   * Props a block draws that are not bound to data — a fixed label, a caption
   * typed by the designer. They are not in `textSegments` once they carry no
   * token, so the plain prop values are what says the block is not empty.
   */
  function staticProps(node: ResolvedNode, skip: string): boolean {
    for (const [prop, value] of Object.entries(node.props)) {
      if (prop === skip || prop.startsWith("__")) continue;
      if (node.bindings[prop]) continue;
      if (typeof value === "string" && value.trim() !== "") return true;
    }
    return false;
  }

  function hasOwnContent(node: ResolvedNode): boolean {
    return ownText(node) !== "";
  }

  /** Does this block have any prop whose whole job is to carry words? */
  function carriesText(node: ResolvedNode): boolean {
    return Object.keys(node.textSegments).length > 0 || Object.keys(node.richText).length > 0;
  }

  function subtreeHasContent(node: ResolvedNode): boolean {
    if (DECORATION.has(node.type)) return false;
    const def = registry.get(node.type);
    // Charts and images say something without saying words.
    if (def?.category === "visualisation" && !DECORATION.has(node.type)) return true;
    if (node.type === "image" || node.type === "logo" || node.type === "signature") return true;
    if (hasOwnContent(node)) return true;
    return node.children.some(subtreeHasContent);
  }
}

/** Every inline node in a rich text document, paragraphs and list items alike. */
function inlinesOf(doc: RichTextDoc): InlineNode[] {
  const out: InlineNode[] = [];
  for (const block of doc.content) {
    const paras = block.type === "paragraph" ? [block] : block.content.flatMap((li) => li.content);
    for (const p of paras) out.push(...p.content);
  }
  return out;
}

/** Blocks that occupy space without carrying information. */
const DECORATION = new Set(["spacer", "divider", "pageBreak"]);

/**
 * Whether an expression has already dealt with the value being missing.
 *
 * `{{ periodEnd ? "as at " + periodEnd : "" }}` evaluating to nothing is the
 * author's intent, not a hole; so is a `default:` fallback. Reporting those
 * would mean the check fires precisely when someone has fixed the problem,
 * which is the fastest way to teach people to ignore a panel.
 */
function handlesEmpty(expr: string): boolean {
  try {
    return guards(parseExpression(expr));
  } catch {
    return false;
  }
}

function guards(node: Expr): boolean {
  switch (node.kind) {
    case "conditional":
      return true;
    case "pipe":
      return node.name === "default" || guards(node.input);
    case "binary":
      // `a or b` and `a and b` are the same intent written shorter.
      return node.op === "or" || node.op === "and" || guards(node.left) || guards(node.right);
    case "call":
      return node.name === "coalesce" || node.name === "default";
    default:
      return false;
  }
}

/** Expressions belong in a tooltip, not mid-sentence; keep the message readable. */
function short(expr: string): string {
  return expr.length > 48 ? `${expr.slice(0, 47)}…` : expr;
}

/** Empty means absent or blank — never `0` and never `false`. */
function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  return false;
}

function getPath(obj: Record<string, unknown>, path: string): unknown {
  let cur: unknown = obj;
  for (const p of path.split(".")) {
    if (cur === null || cur === undefined || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}
